const PENDING_KEY = 'nlims_pending_writes';
const PENDING_EVENT = 'nlims:pending-writes-changed';

function getStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function notifyChange() {
  if (typeof window === 'undefined') return;

  try {
    window.dispatchEvent(new CustomEvent(PENDING_EVENT));
  } catch {
    // Ignore environments that cannot dispatch custom events.
  }
}

function readQueue() {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(PENDING_KEY, JSON.stringify(queue));
  } catch {
    // Best-effort cache only.
  }

  notifyChange();
}

function normalizeEntry(entry = {}) {
  const createdAt = entry.createdAt ?? new Date().toISOString();
  return {
    id: String(entry.id ?? createOfflineUuid('write')).trim(),
    collectionKey: String(entry.collectionKey ?? '').trim() || null,
    action: String(entry.action ?? '').trim() || 'create',
    method: String(entry.method ?? 'post').trim().toLowerCase() || 'post',
    url: String(entry.url ?? '').trim(),
    data: entry.data ?? null,
    params: entry.params ?? null,
    tempUuid: String(entry.tempUuid ?? '').trim() || null,
    recordUuid: String(entry.recordUuid ?? entry.tempUuid ?? '').trim() || null,
    createdAt,
    attempts: Number.isFinite(Number(entry.attempts)) ? Number(entry.attempts) : 0,
    lastError: entry.lastError ?? null,
  };
}

function unwrapPayload(response) {
  const payload = response?.data ?? response ?? {};
  return payload?.data ?? payload;
}

function inspectForUuid(value) {
  if (!value || typeof value !== 'object') return null;

  const candidates = [
    value.uuid,
    value.id,
    value.item,
    value.record,
    value.data,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      const text = String(candidate).trim();
      if (text) return text;
    }
    const nested = inspectForUuid(candidate);
    if (nested) return nested;
  }

  if (Array.isArray(value.items) && value.items.length > 0) {
    return inspectForUuid(value.items[0]);
  }

  if (Array.isArray(value.results) && value.results.length > 0) {
    return inspectForUuid(value.results[0]);
  }

  return null;
}

export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function createOfflineUuid(prefix = 'offline') {
  const safePrefix = String(prefix ?? 'offline')
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '') || 'offline';

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${safePrefix}-${crypto.randomUUID()}`;
  }

  return `${safePrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function readPendingWrites() {
  return readQueue();
}

export function getPendingWritesCount() {
  return readQueue().length;
}

export function enqueuePendingWrite(entry = {}) {
  const nextEntry = normalizeEntry(entry);
  const nextQueue = [...readQueue(), nextEntry];
  writeQueue(nextQueue);
  return nextEntry;
}

export function updatePendingWrite(id, patch = {}) {
  const key = String(id ?? '').trim();
  if (!key) return null;

  const nextQueue = readQueue().map((entry) =>
    String(entry.id ?? '').trim() === key
      ? normalizeEntry({ ...entry, ...patch, id: entry.id ?? key })
      : entry
  );

  writeQueue(nextQueue);
  return nextQueue.find((entry) => String(entry.id ?? '').trim() === key) ?? null;
}

export function removePendingWrite(id) {
  const key = String(id ?? '').trim();
  if (!key) return readQueue();

  const nextQueue = readQueue().filter((entry) => String(entry.id ?? '').trim() !== key);
  writeQueue(nextQueue);
  return nextQueue;
}

export function clearPendingWrites() {
  writeQueue([]);
}

export function resolveResponseUuid(response) {
  return inspectForUuid(unwrapPayload(response));
}

export async function flushPendingWrites(executor, { onSuccess, onFailure } = {}) {
  const pending = readQueue();
  if (pending.length === 0) return { flushed: 0, remaining: 0 };

  const remaining = [];
  let flushed = 0;

  for (const item of pending) {
    try {
      const result = await executor(item);
      flushed += 1;
      onSuccess?.(item, result);
    } catch (error) {
      const failed = normalizeEntry({
        ...item,
        attempts: Number(item.attempts ?? 0) + 1,
        lastError: error?.message ?? String(error ?? 'Offline sync failed.'),
      });
      remaining.push(failed);
      onFailure?.(failed, error);
    }
  }

  writeQueue(remaining);
  return { flushed, remaining: remaining.length };
}

export function getPendingWritesEventName() {
  return PENDING_EVENT;
}

